import { FC } from 'react'
import styled from 'styled-components'
import { Icon, IconColor, IconTypes } from '@app/ui'
import { useBrandConfig } from '@app/hooks/useBrandConfig'
import { useTranslation } from 'react-i18next'

interface EntryFlowTipsterInfoProps {
  className?: string
}

const EntryFlowTipsterInfo: FC<EntryFlowTipsterInfoProps> = ({ className }) => {
  const { t } = useTranslation()
  const { tipsterInfo, showPoweredBy } = useBrandConfig()

  const socialLinks = [
    { url: tipsterInfo?.instagramUrl, icon: IconTypes.instagramSocial },
    { url: tipsterInfo?.telegramUrl, icon: IconTypes.telegramSocial },
    { url: tipsterInfo?.xUrl, icon: IconTypes.xIcon },
    { url: tipsterInfo?.linkedinUrl, icon: IconTypes.linkedinSocial },
    { url: tipsterInfo?.websiteUrl, icon: IconTypes.internetIcon },
  ].filter(link => link.url)


  return (
    <Container className={className}>
      {showPoweredBy !== false && (
        <Header>
          <BrandIconWrapper>
            <Icon
              type={IconTypes.brand}
              width={29}
              height={29}
              color={IconColor.white}
            />
          </BrandIconWrapper>
          <PoweredByText>{t('entryFlowTipsterInfo.poweredBy')}<BrandNameText>TIPSTERIZER</BrandNameText></PoweredByText>
        </Header>
      )}

      <CenteredContent>
        <TipsterImageWrapper>
          {tipsterInfo?.imageUrl ? (
            <TipsterImage src={tipsterInfo.imageUrl} alt={tipsterInfo?.displayName || 'Tipster'} />
          ) : (
            <TipsterImagePlaceholder>
              <Icon
                type={IconTypes.personCircle}
                width={124}
                height={124}
                color={IconColor.viloet400}
              />
            </TipsterImagePlaceholder>
          )}
        </TipsterImageWrapper>

        <TipsterDisplayName>{tipsterInfo?.displayName}</TipsterDisplayName>

        <TipsterDescription>{tipsterInfo?.description}</TipsterDescription>

        {socialLinks.length > 0 && (
          <SocialLinksContainer>
            {socialLinks.map((link, index) => (
              <SocialLink key={index} href={link.url} target="_blank" rel="noopener noreferrer">
                <Icon
                  type={link.icon}
                  width={16}
                  height={16}
                  color={IconColor.viloet400}
                />
              </SocialLink>
            ))}
          </SocialLinksContainer>
        )}
      </CenteredContent>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  width: 100%;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const BrandIconWrapper = styled.div`
  display: flex;
  align-items: center;
`

const CenteredContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
`

const PoweredByText = styled.span`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 400;
  color: var(--text-primary, #FFFFFF);
  line-height: 1.2;
`

const BrandNameText = styled.span`
  font-family: 'Michroma';
  font-weight: 500;
  font-size: var( --font-size-larger);
  color: var(--text-primary, #FFFFFF);
`

const TipsterImageWrapper = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--violet-600);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 9.92px 19.84px 0px #00000080;
  background: linear-gradient(180deg, rgba(var(--violet-600-rgb), 0.2) 0%, rgba(var(--violet-600-rgb), 0.1) 100%);
`

const TipsterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const TipsterImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  height: 124px;
  width: 124px;
`

const TipsterDisplayName = styled.h2`
  font-size: var(--font-size-extra-big);
  font-weight: 400;
  color: var(--text-primary, #FFFFFF);
  text-align: center;
  margin-bottom: 8px;
`

const TipsterDescription = styled.p`
  font-weight: 300;
  color: var(--text-primary, #FFFFFF);
  text-align: center;
  max-width: 280px;
  line-height: 1.5;
  margin-bottom: 24px;
`

const SocialLinksContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-secondary);
  padding: 8px;
  border-radius: 200px;
  margin-bottom: 24px;
`

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 81px;
  background: var(--bg-quaternary);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
  text-decoration: none;

  &:hover {
    background: var(--bg-septenary);
  }

  &:active {
    transform: scale(0.95);
  }
`

export default EntryFlowTipsterInfo
